"use client";

import type { ArticleComponentConfig } from "@/blog/models";
import React, { createContext, useContext, useEffect, useState } from "react";

export interface ArticleFilter {
  category: string;
  query: string;
  page: number;
  visible: number;
}
export const initialArticleFilter: ArticleFilter = {
  category: "",
  query: "",
  page: 1,
  visible: 0,
};
const ArticleInteractions = createContext<{
  filters: Record<string, ArticleFilter>;
  update: (key: string, change: Partial<ArticleFilter>) => void;
  listings: Record<string, ArticleComponentConfig>;
}>({ filters: {}, update: () => {}, listings: {} });

export function ArticleInteractionProvider({
  children,
  listings = {},
}: {
  children: React.ReactNode;
  listings?: Record<string, ArticleComponentConfig>;
}) {
  const [filters, setFilters] = useState<Record<string, ArticleFilter>>({});
  useEffect(() => {
    const load = () => {
      const next: Record<string, ArticleFilter> = {};
      new URLSearchParams(window.location.search).forEach((value, name) => {
        const match = name.match(
          /^article\.(.+)\.(category|query|page|visible)$/,
        );
        if (!match) return;
        const [, key, field] = match;
        const state = next[key] ?? { ...initialArticleFilter };
        if (field === "category" || field === "query") state[field] = value;
        else
          state[field as "page" | "visible"] = Math.max(
            1,
            Math.min(10000, Number(value) || 1),
          );
        next[key] = state;
      });
      setFilters(next);
    };
    load();
    window.addEventListener("popstate", load);
    return () => window.removeEventListener("popstate", load);
  }, []);
  const update = (key: string, change: Partial<ArticleFilter>) => {
    setFilters((previous) => {
      const state = { ...initialArticleFilter, ...previous[key], ...change };
      const url = new URL(window.location.href);
      Object.entries(state).forEach(([field, value]) => {
        const name = `article.${key}.${field}`;
        if (value === initialArticleFilter[field as keyof ArticleFilter])
          url.searchParams.delete(name);
        else url.searchParams.set(name, String(value));
      });
      window.history.replaceState(null, "", url);
      return { ...previous, [key]: state };
    });
  };
  return (
    <ArticleInteractions.Provider value={{ filters, update, listings }}>
      {children}
    </ArticleInteractions.Provider>
  );
}

export function useArticleFilter(key: string) {
  const { filters, update, listings } = useContext(ArticleInteractions);
  return {
    filter: filters[key] ?? initialArticleFilter,
    listing: listings[key],
    update: (change: Partial<ArticleFilter>) => update(key, change),
  };
}
